import React, { useState } from 'react';
import { EventsDisplayProps } from '../../types/props';
import { CalendarEvent } from '../../types/contexts';

const USE_UNTAGGED_EVENTS = false;

interface RangeResult {
  topEvents: [string, number][];
  remainingHours: number;
  remainingCount: number;
  uniqueCount: number;
}

const EventsDisplay: React.FC<EventsDisplayProps> = ({ tags, tagKeys, dateFilterHours, dateSelectionString }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const createRange = (events: CalendarEvent[]): RangeResult => {
    // Consolidate events with the same summary by aggregating their durations
    const consolidated = new Map<string, number>();
    events.forEach(event => {
      const key = event.summary;
      consolidated.set(key, (consolidated.get(key) || 0) + event.duration);
    });

    // Sort consolidated events by total duration in descending order
    const sortedEvents = Array.from(consolidated.entries())
      .sort(([, a], [, b]) => b - a);

    const uniqueCount = sortedEvents.length;

    return {
      topEvents: sortedEvents.slice(0, 5),
      remainingHours: sortedEvents.slice(5).reduce((sum, [, duration]) => sum + duration, 0),
      remainingCount: Math.max(0, sortedEvents.length - 5),
      uniqueCount
    };
  };

  const calculatePercentage = (hours: number, totalHours: number): string => {
    if (totalHours === 0) return '0.00';
    return ((hours / totalHours) * 100).toFixed(2);
  };

  // Build a unified list of groups: hashtag tags + one "untagged" group
  const hashtagKeys = tagKeys.filter(key => key.startsWith('#'));
  const untaggedKeys = tagKeys.filter(key => !key.startsWith('#'));

  interface GroupEntry {
    key: string;
    label: string;
    events: CalendarEvent[];
    totalHours: number;
  }

  const groups: GroupEntry[] = [];

  // Add hashtag groups
  hashtagKeys.forEach(key => {
    const events = tags[key];
    const totalHours = events.reduce((sum: number, event: CalendarEvent) => sum + event.duration, 0);
    groups.push({ key, label: key.toUpperCase(), events, totalHours });
  });

  // Add untagged group (merge all untagged keys into one)
  if (USE_UNTAGGED_EVENTS && untaggedKeys.length > 0) {
    const allUntaggedEvents = untaggedKeys.flatMap(key => tags[key]);
    const totalHours = allUntaggedEvents.reduce((sum: number, event: CalendarEvent) => sum + event.duration, 0);
    groups.push({ key: '__untagged__', label: 'UNTAGGED', events: allUntaggedEvents, totalHours });
  }

  // Sort all groups by total hours descending
  const sortedGroups = [...groups].sort((a, b) => b.totalHours - a.totalHours);

  const topGroups = sortedGroups.slice(0, 3);
  const remainingGroups = sortedGroups.slice(3);

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">{dateSelectionString}</h2>

      {/* Top 3 groups as full boxes */}
      {topGroups.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {topGroups.map(group => {
            const { topEvents, remainingHours, remainingCount, uniqueCount } = createRange(group.events);

            return (
              <div key={group.key} className="bg-white shadow rounded-lg p-4">
                <h3 className="text-xl font-semibold mb-2">{group.label}</h3>
                <h4 className="text-md text-gray-600 mb-2">
                  Top {Math.min(uniqueCount, 5)} events by hours:
                  <span className="float-right">Hours</span>
                </h4>
                <ul className="mb-4">
                  {topEvents.map(([summary, duration], index) => (
                    <li key={index} className="flex justify-between">
                      <span>{summary}</span>
                      <span>{duration.toFixed(2)}</span>
                    </li>
                  ))}
                  {remainingCount > 0 && (
                    <li className="flex justify-between text-gray-500 mt-2 pt-2 border-t">
                      <span>{remainingCount} other events</span>
                      <span>{remainingHours.toFixed(2)}</span>
                    </li>
                  )}
                </ul>
                <p className="text-sm text-gray-600">Total hours: {group.totalHours.toFixed(2)}</p>
                <p className="text-sm text-gray-600">
                  Percentage of time invested: {calculatePercentage(group.totalHours, dateFilterHours)}%
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Remaining groups as expandable accordion rows */}
      {remainingGroups.length > 0 && (
        <div className="space-y-2 mb-6">
          {remainingGroups.map(group => {
            const isExpanded = expandedGroups.has(group.key);
            const { topEvents, remainingHours, remainingCount, uniqueCount } = createRange(group.events);
            return (
              <div key={group.key} className="bg-white shadow rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleGroup(group.key)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold">{group.label}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      {group.totalHours.toFixed(2)} hrs · {calculatePercentage(group.totalHours, dateFilterHours)}%
                    </span>
                    <span className="text-gray-400 text-sm">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <h4 className="text-md text-gray-600 mb-2 pt-3">
                      Top {Math.min(uniqueCount, 5)} events by hours:
                      <span className="float-right">Hours</span>
                    </h4>
                    <ul className="mb-2">
                      {topEvents.map(([summary, duration], index) => (
                        <li key={index} className="flex justify-between">
                          <span>{summary}</span>
                          <span>{duration.toFixed(2)}</span>
                        </li>
                      ))}
                      {remainingCount > 0 && (
                        <li className="flex justify-between text-gray-500 mt-2 pt-2 border-t">
                          <span>{remainingCount} other events</span>
                          <span>{remainingHours.toFixed(2)}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventsDisplay;
